// Option lists for the timezone / currency pickers, built from the browser's
// own Intl data so they cover every IANA zone and ISO 4217 currency without
// hand-maintained lists. Falls back to a short static set on old browsers
// that lack Intl.supportedValuesOf (pre-2022).

const FALLBACK_TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Europe/Istanbul', 'Asia/Dubai', 'Asia/Tokyo', 'Asia/Shanghai',
  'Asia/Kolkata', 'Australia/Sydney',
];

const FALLBACK_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'TRY', 'JPY', 'CNY', 'CHF', 'AED', 'SAR', 'INR'];

// Current UTC offset of a zone, e.g. "GMT+3" — shown as the option sublabel.
function tzOffsetLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch (_e) {
    return '';
  }
}

export function getTimezoneOptions() {
  let zones;
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch (_e) {
    zones = FALLBACK_TIMEZONES;
  }
  // supportedValuesOf omits plain "UTC" in some engines — keep it first either way.
  return ['UTC', ...zones.filter((z) => z !== 'UTC')].map((tz) => ({
    value: tz,
    label: tz.replace(/_/g, ' '),
    sublabel: tzOffsetLabel(tz),
  }));
}

// ISO 3166-1 alpha-2 country codes. Intl.supportedValuesOf has no 'region'
// key, so the code list itself is static; display names come from Intl and
// follow the UI language.
const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR',
  'BS', 'BT', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM',
  'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB',
  'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GT', 'GU', 'GW',
  'GY', 'HK', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS',
  'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY',
  'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD',
  'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU',
  'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR',
  'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PR', 'PS', 'PT', 'PW',
  'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SI', 'SK',
  'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TG', 'TH',
  'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'US', 'UY',
  'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
];

export function getCountryOptions(locale = 'en') {
  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'region' });
  } catch (_e) { /* label falls back to the bare code */ }
  return COUNTRY_CODES
    .map((code) => {
      let name = '';
      try {
        name = displayNames?.of(code) || '';
      } catch (_e) { /* unknown region in this locale's data */ }
      return { value: code, label: name && name !== code ? name : code, sublabel: code };
    })
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

export function getCurrencyOptions(locale = 'en') {
  let codes;
  try {
    codes = Intl.supportedValuesOf('currency');
  } catch (_e) {
    codes = FALLBACK_CURRENCIES;
  }
  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'currency' });
  } catch (_e) { /* label falls back to the bare code */ }
  return codes.map((code) => {
    let name = '';
    try {
      name = displayNames?.of(code) || '';
    } catch (_e) { /* unknown code in this locale's data */ }
    return {
      value: code,
      label: name && name !== code ? `${code} — ${name}` : code,
    };
  });
}
